const AUTH_SESSION_KEY = 'artha_auth_session_v1';
const AUTH_DB_NAME = 'artha_auth_db';
const AUTH_STORE_NAME = 'auth_store';
const AUTH_LOCAL_FALLBACK_KEY = 'artha_auth_fallback_v1';
const LEGACY_USER_KEY = 'user';
const LEGACY_LOGIN_TIME_KEY = 'login_time';
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

type AuthSessionPayload = {
    user: any;
    issuedAt: number;
};

function parsePayload(raw: string | null): AuthSessionPayload | null {
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as AuthSessionPayload;
        if (!parsed || !parsed.user || typeof parsed.issuedAt !== 'number') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function isValidSession(payload: AuthSessionPayload): boolean {
    return (Date.now() - payload.issuedAt) <= SESSION_TTL_MS;
}

function canUseIndexedDb(): boolean {
    return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function openAuthDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        if (!canUseIndexedDb()) {
            reject(new Error('IndexedDB is not available'));
            return;
        }

        const request = window.indexedDB.open(AUTH_DB_NAME, 1);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
                db.createObjectStore(AUTH_STORE_NAME);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
    });
}

async function idbSet(key: string, value: string): Promise<void> {
    const db = await openAuthDb();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(AUTH_STORE_NAME, 'readwrite');
        tx.objectStore(AUTH_STORE_NAME).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error || new Error('Failed to write IndexedDB value'));
        tx.onabort = () => reject(tx.error || new Error('IndexedDB write aborted'));
    });
    db.close();
}

async function idbGet(key: string): Promise<string | null> {
    const db = await openAuthDb();
    const result = await new Promise<string | null>((resolve, reject) => {
        const tx = db.transaction(AUTH_STORE_NAME, 'readonly');
        const req = tx.objectStore(AUTH_STORE_NAME).get(key);
        req.onsuccess = () => resolve((req.result as string) || null);
        req.onerror = () => reject(req.error || new Error('Failed to read IndexedDB value'));
    });
    db.close();
    return result;
}

async function idbDelete(key: string): Promise<void> {
    const db = await openAuthDb();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(AUTH_STORE_NAME, 'readwrite');
        tx.objectStore(AUTH_STORE_NAME).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error || new Error('Failed to delete IndexedDB value'));
        tx.onabort = () => reject(tx.error || new Error('IndexedDB delete aborted'));
    });
    db.close();
}

function fallbackSave(payload: AuthSessionPayload) {
    localStorage.setItem(AUTH_LOCAL_FALLBACK_KEY, JSON.stringify(payload));
}

function fallbackLoad(): AuthSessionPayload | null {
    return parsePayload(localStorage.getItem(AUTH_LOCAL_FALLBACK_KEY));
}

function fallbackClear() {
    localStorage.removeItem(AUTH_LOCAL_FALLBACK_KEY);
}

function clearLegacyStorage() {
    localStorage.removeItem(LEGACY_USER_KEY);
    localStorage.removeItem(LEGACY_LOGIN_TIME_KEY);
}

export const AuthSession = {
    async save(user: any) {
        const payload: AuthSessionPayload = {
            user,
            issuedAt: Date.now()
        };

        const payloadRaw = JSON.stringify(payload);
        try {
            await idbSet(AUTH_SESSION_KEY, payloadRaw);
            fallbackClear();
        } catch {
            fallbackSave(payload);
        }

        clearLegacyStorage();
    },

    async load() {
        let payload: AuthSessionPayload | null = null;

        try {
            const raw = await idbGet(AUTH_SESSION_KEY);
            payload = parsePayload(raw);
        } catch {
            payload = fallbackLoad();
        }

        if (payload && isValidSession(payload)) {
            return payload.user;
        }

        await this.clear();

        // One-time migration from legacy localStorage keys.
        const legacyRawUser = localStorage.getItem(LEGACY_USER_KEY);
        const legacyLoginTime = localStorage.getItem(LEGACY_LOGIN_TIME_KEY);
        if (!legacyRawUser || !legacyLoginTime) {
            return null;
        }

        try {
            const loginAt = new Date(legacyLoginTime).getTime();
            if (Number.isNaN(loginAt) || (Date.now() - loginAt) > SESSION_TTL_MS) {
                clearLegacyStorage();
                return null;
            }

            const legacyUser = JSON.parse(legacyRawUser);
            await this.save(legacyUser);
            return legacyUser;
        } catch {
            clearLegacyStorage();
            return null;
        }
    },

    async clear() {
        try {
            await idbDelete(AUTH_SESSION_KEY);
        } catch {
            // Fallback clear runs regardless.
        }
        fallbackClear();
        clearLegacyStorage();
    }
};
